/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description  insurance utils
 */
import _ from 'lodash';
import { Utils } from '@react-awesome-query-builder/antd';
import { findValueByIdentifierCode, getNameMeta, isStartByHash, removeFirstHash } from 'utils/claims/institutionalClaimRequestParser';
import systemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { PAYER_RESPONSIBILITY_OPTIONS, payerResponsibilitySequenceMapping } from 'app-constants/claims/claimRequest';

// codes mapping
const codesMapping = {
	groupNumber: '6P',
	memberId: 'MI',
	self: '18',
};

// get filtered insurance info
const getFilteredInsuranceInfo = ({
	insurance,
	isActive,
}: {
	insurance: Insurance.IPaginatedResponseModel;
	isActive: boolean;
}): Insurance.ICoverageResponseModel[] => {
	return _.filter(insurance?.data, (item) => {
		return isActive ? _.isNumber(item?.fhirResource?.order) : !_.isNumber(item?.fhirResource?.order);
	});
};

// get existing payer responsibility
const getExistingPayerResponsibility = (insurance: Insurance.ICoverageResponseModel[]): string[] => {
	const allPayerResponsibility = _.map(insurance, (item) => {
		const payerResponsibility = _.find(
			item?.fhirResource?.extension,
			(ext) => ext?.url === systemMapping?.responsibilitySequence,
		)?.valueString;
		if (payerResponsibility && payerResponsibility !== payerResponsibilitySequenceMapping.unknown.code) {
			return payerResponsibility;
		}
		return null;
	});
	return _.uniq(_.compact(allPayerResponsibility));
};

// get categorized insurance
const getCategorizedInsurance = (
	insurance: Insurance.ICoverageResponseModel[],
): {
	currentInsurance: Insurance.ICoverageResponseModel[];
	oldInsurance: Insurance.ICoverageResponseModel[];
	existingPayerResponsibility: string[];
} => {
	const currentInsurance = _.filter(insurance, (item) => item?.fhirResource?.status !== 'cancelled');
	const oldInsurance = _.filter(insurance, (item) => item?.fhirResource?.status === 'cancelled');
	const existingPayerResponsibility = getExistingPayerResponsibility(currentInsurance);
	return { currentInsurance, oldInsurance, existingPayerResponsibility };
};

// get fhir resource
const getFhirResource = (coverage: Insurance.ICoverageResponseModel, valueReference: string): AnyType => {
	if (isStartByHash(valueReference)) {
		return _.find(coverage?.fhirResource?.contained, (contained) => contained?.id === removeFirstHash(valueReference));
	}
	return _.get(coverage?.metadata, valueReference);
};

// coverage modal parser
const getSubscriberInfo = (coverage: Insurance.ICoverageResponseModel): Insurance.ISubscriberInfo => {
	const fhirCoverage = coverage?.fhirResource;
	const subscriberRef = fhirCoverage?.subscriber;
	const subscriber = getFhirResource(coverage, subscriberRef?.reference);

	const subscriberName = getNameMeta(subscriber?.name)?.[0]?.fullName;
	const subscriberDob = subscriber?.birthDate;
	const relationshipCode = coverage?.fhirResource?.relationship?.coding?.[0]?.code;
	const memberId = findValueByIdentifierCode(fhirCoverage?.subscriberId, 'MI', systemMapping['NM1-08'], systemMapping['NM1-09'])?.value;

	return {
		subscriberName,
		subscriberDob,
		relationshipCode,
		memberId,
		address: subscriber?.address?.[0]?.line?.[0],
	};
};

// get insurance plan info
const getInsurancePlanInfo = (coverage: Insurance.ICoverageResponseModel): Insurance.IInsurancePlanInfo => {
	const insurance = getFhirResource(coverage, coverage?.fhirResource?.insurancePlan?.reference) as Fhir.InsurancePlan;
	const planName = insurance?.name;
	const insuranceCoding = insurance?.type?.[0]?.coding?.[0];
	const insuranceTypeCode = insuranceCoding?.code;
	return {
		planName,
		insuranceTypeCode,
	};
};

// get other meta
const getOtherMeta = (coverage: Insurance.ICoverageResponseModel): Insurance.IOtherMeta => {
	const coverageResource = coverage?.fhirResource;
	const groupNumber = findValueByIdentifierCode(
		coverageResource?.identifier,
		codesMapping.groupNumber,
		systemMapping['REF-01'],
		systemMapping['REF-02'],
	)?.value;

	const versionId = coverage?.fhirResource?.meta?.versionId;

	return {
		groupNumber,
		versionId,
	};
};

// get extension meta
const getExtensionMeta = (coverage: Insurance.ICoverageResponseModel): Insurance.IExtensionMeta => {
	const extension = coverage?.fhirResource?.extension;
	// if order is there, pick responsibility sequence else it is null
	const responsibilitySequence = coverage?.fhirResource?.order
		? _.find(extension, (item) => item?.url === systemMapping?.responsibilitySequence)?.valueString
		: null;
	const providerAcceptAssignment = _.find(extension, (item) => item?.url === systemMapping?.providerAcceptAssignment)?.valueString;
	const assignmentOfBenefitAuthorized = _.find(
		extension,
		(item) => item?.url === systemMapping?.assignmentOfBenefitAuthorized,
	)?.valueString;
	const relationshipToGuarantor = _.find(extension, (item) => item?.url === systemMapping?.relationshipToGuarantor)?.valueString;
	const authorizationToReleaseMedicalInformation = _.find(
		extension,
		(item) => item?.url === systemMapping?.authorizationToReleaseMedicalInformation,
	)?.valueString;
	const remarks = _.find(extension, (item) => item?.url === systemMapping?.remarks)?.valueString;

	return {
		responsibilitySequence,
		providerAcceptAssignment,
		assignmentOfBenefitAuthorized,
		relationshipToGuarantor,
		authorizationToReleaseMedicalInformation,
		remarks,
	};
};

// get guarantor info
const getGuarantorInfo = (coverage: Insurance.ICoverageResponseModel): Insurance.IGuarantorInfo => {
	const guarantorRef = _.find(coverage?.fhirResource?.paymentBy, (item) => item?.responsibility === 'guarantor')?.party?.reference;
	const guarantor = getFhirResource(coverage, guarantorRef);
	const name = _.isString(guarantor?.name) ? guarantor?.name : getNameMeta(guarantor?.name)?.[0]?.fullName;

	return {
		name,
	};
};

// get payer info
const getPayerInfo = (coverage: Insurance.ICoverageResponseModel): Insurance.IPayerInfo => {
	const payerRef = coverage?.fhirResource?.insurer?.reference;
	const payer = getFhirResource(coverage, payerRef) as Fhir.Organization;
	const payerId = payer?.id;

	return {
		payerId,
		payerName: payer?.name,
	};
};

// get parsed coverage info
const getParsedCoverageInfo = (coverage: Insurance.ICoverageResponseModel): Insurance.IParsedCoverageInfo => {
	const coverageResource = coverage?.fhirResource;
	const coverageStartDate = coverageResource?.period?.start;
	const coverageEndDate = coverageResource?.period?.end;

	const subscriberInfo = getSubscriberInfo(coverage);
	const insurancePlanInfo = getInsurancePlanInfo(coverage);
	const otherMeta = getOtherMeta(coverage);
	const guarantorInfo = getGuarantorInfo(coverage);
	const extensionMeta = getExtensionMeta(coverage);
	const payerInfo = getPayerInfo(coverage);

	return {
		coverageId: coverage?.fhirResource?.id,
		coverageStartDate,
		coverageEndDate,
		subscriberInfo,
		insurancePlanInfo,
		otherMeta,
		extensionMeta,
		guarantorInfo,
		payerInfo,
		metadata: coverage?.metadata,
		coverageEligibilityResponse: coverage?.coverageEligibilityResponse,
		fhirCoverage: coverage?.fhirResource,
	};
};

// prepare contained reference
const prepareContainedReference = (ref: string): string => {
	return `#${ref}`;
};

// get reference id
const getReferenceId = (reference?: string): string | null => {
	if (!reference) {
		return null;
	}
	if (isStartByHash(reference)) {
		return removeFirstHash(reference);
	}
	return _.last(reference?.split('/'));
};

// get initial model
const getInitialModel = (parsedCoverage: Insurance.IParsedCoverageInfo): Insurance.IFormModel => {
	return {
		responsibilitySequence: parsedCoverage?.extensionMeta?.responsibilitySequence || undefined,
		payer: parsedCoverage?.payerInfo?.payerId || undefined,
		memberId: parsedCoverage?.subscriberInfo?.memberId || '',
		insuranceType: parsedCoverage?.insurancePlanInfo?.insuranceTypeCode || undefined,
		subscriberName: parsedCoverage?.subscriberInfo?.subscriberName || '',
		subscriberAddress: parsedCoverage?.subscriberInfo?.address || '',
		subscriberDateOfBirth: parsedCoverage?.subscriberInfo?.subscriberDob || '',
		relationshipToSubscriber: parsedCoverage?.subscriberInfo?.relationshipCode || codesMapping.self,
		groupNumber: parsedCoverage?.otherMeta?.groupNumber || '',
		planName: parsedCoverage?.insurancePlanInfo?.planName || '',
		coverageStartDate: parsedCoverage?.coverageStartDate || '',
		coverageEndDate: parsedCoverage?.coverageEndDate || '',
		guarantorName: parsedCoverage?.guarantorInfo?.name || '',
		relationshipToGuarantor: parsedCoverage?.extensionMeta?.relationshipToGuarantor || undefined,
		providerAcceptAssignment: parsedCoverage?.extensionMeta?.providerAcceptAssignment || undefined,
		assignmentOfBenefitAuthorized: parsedCoverage?.extensionMeta?.assignmentOfBenefitAuthorized || undefined,
		authorizationToReleaseMedicalInformation: parsedCoverage?.extensionMeta?.authorizationToReleaseMedicalInformation || undefined,
		remarks: parsedCoverage?.extensionMeta?.remarks || '',
	};
};

// prepare submit model
const prepareSubmitModel = ({
	model,
	patientId,
	olderCoverage,
}: {
	model: Insurance.IFormModel;
	patientId: string;
	olderCoverage: Fhir.Coverage;
}): Fhir.Coverage => {
	const coverageResource: Fhir.Coverage = {
		resourceType: 'Coverage',
		status: 'active',
		kind: 'insurance',
		beneficiary: {
			reference: `Patient/${patientId}`,
		},
		id: Utils.uuid(),
		...(olderCoverage || {}),
		contained: null,
	};
	const extensionMeta: Fhir.Extension[] = [
		{
			url: systemMapping?.responsibilitySequence,
			valueString: model?.responsibilitySequence,
		},
		{ url: systemMapping?.providerAcceptAssignment, valueString: model?.providerAcceptAssignment },
		{ url: systemMapping?.assignmentOfBenefitAuthorized, valueString: model?.assignmentOfBenefitAuthorized },
		{ url: systemMapping?.relationshipToGuarantor, valueString: model?.relationshipToGuarantor },
		{ url: systemMapping?.authorizationToReleaseMedicalInformation, valueString: model?.authorizationToReleaseMedicalInformation },
		{ url: systemMapping?.remarks, valueString: model?.remarks },
	];
	/* provider accept assignment, relationship to guarantor, assignment of benefit authorized, authorization to
	 release medical information, remarks, insurance responsibility sequence will go in extension */
	const filteredExtensionMeta = _.filter(extensionMeta, (item) => item?.valueString) as Fhir.Extension[];
	coverageResource.extension = _.isEmpty(filteredExtensionMeta) ? null : filteredExtensionMeta;

	// coverage Period
	coverageResource.period = {
		start: model?.coverageStartDate || null,
		end: model?.coverageEndDate || null,
	};

	// identifier for group number
	const existingGroupNumber = _.find(
		coverageResource.identifier,
		(item) => item?.type?.coding?.[0]?.code === codesMapping.groupNumber,
	)?.value;
	if (model?.groupNumber && existingGroupNumber !== model?.groupNumber) {
		const filteredIdentifier = _.filter(
			coverageResource.identifier,
			(item) => item?.type?.coding?.[0]?.code === codesMapping.groupNumber,
		);
		coverageResource.identifier = [
			...(filteredIdentifier || []),
			{
				type: { coding: [{ system: systemMapping['REF-01'], code: codesMapping.groupNumber }] },
				system: systemMapping['REF-02'],
				value: model?.groupNumber,
			},
		];
	} else {
		coverageResource.identifier = olderCoverage?.identifier || null;
	}

	// subscriber id
	const existingSubscriberId = _.find(
		coverageResource.subscriberId,
		(item) => item?.type?.coding?.[0]?.code === codesMapping.memberId,
	)?.value;
	if (model?.memberId && existingSubscriberId !== model?.memberId) {
		coverageResource.subscriberId = [
			{
				type: { coding: [{ system: systemMapping['NM1-08'], code: codesMapping.memberId }] },
				system: systemMapping['NM1-09'],
				value: model?.memberId,
			},
		];
	} else {
		coverageResource.subscriberId = olderCoverage?.subscriberId || null;
	}

	// relation to subscriber
	const relationToSubscriber = model?.relationshipToSubscriber;
	if (relationToSubscriber) {
		coverageResource.relationship = {
			coding: [{ code: relationToSubscriber }],
		};
	} else {
		coverageResource.relationship = null;
	}

	// subscriber data
	const subscriberName = model?.subscriberName?.split(' ');
	const givenName = _.isEmpty(_.dropRight(subscriberName)) ? null : _.dropRight(subscriberName);

	const getSubscriberData = (uuid: string): Fhir.Patient | Fhir.RelatedPerson => {
		const patientIsSubscriber = model?.relationshipToSubscriber === codesMapping.self;
		const resource = {
			id: uuid,
			resourceType: patientIsSubscriber ? 'Patient' : 'RelatedPerson',
			name: _.isEmpty(model?.subscriberName) ? null : [{ given: givenName, family: _.last(subscriberName) }],
			birthDate: model?.subscriberDateOfBirth || null,
			address: _.isEmpty(model?.subscriberAddress) ? null : [{ line: [model?.subscriberAddress] }],
		};
		// if patient is not subscriber, then add patient reference in subscriber resource
		if (!patientIsSubscriber) {
			(resource as Fhir.RelatedPerson).patient = {
				reference: `Patient/${patientId}`,
			};
		}
		return resource as Fhir.Patient | Fhir.RelatedPerson;
	};
	const subscriberUUID = getReferenceId(olderCoverage?.subscriber?.reference) || Utils.uuid();
	coverageResource.subscriber = {
		reference: prepareContainedReference(subscriberUUID),
	};
	coverageResource.contained = [...(coverageResource.contained || []), getSubscriberData(subscriberUUID)];

	// insurance plan
	const getInsurancePlanData = (uuid: string): Fhir.InsurancePlan => {
		return {
			resourceType: 'InsurancePlan',
			id: uuid,
			name: model?.planName || null,
			type: model?.insuranceType ? [{ coding: [{ code: model?.insuranceType }] }] : null,
		};
	};
	const insurancePlanUUID = getReferenceId(olderCoverage?.insurancePlan?.reference) || Utils.uuid();
	coverageResource.insurancePlan = {
		reference: prepareContainedReference(insurancePlanUUID),
	};
	coverageResource.contained = [...(coverageResource.contained || []), getInsurancePlanData(insurancePlanUUID)];

	// guarantor
	const existingGuarantorRef = _.find(coverageResource.paymentBy, (item) => item?.responsibility === 'guarantor')?.party?.reference;
	const getGuarantorData = (uuid: string): Fhir.Organization => {
		return {
			resourceType: 'Organization',
			id: uuid,
			name: model?.guarantorName || null,
		};
	};
	const guarantorUUID = getReferenceId(existingGuarantorRef) || Utils.uuid();
	coverageResource.paymentBy = [
		{
			responsibility: 'guarantor',
			party: { reference: prepareContainedReference(guarantorUUID) },
		},
	];
	coverageResource.contained = [...(coverageResource.contained || []), getGuarantorData(guarantorUUID)];

	// payer reference (payer will always exist in BE so just send ref)
	const payerUUID = getReferenceId(olderCoverage?.insurer?.reference) || model?.payer;
	if (payerUUID) {
		coverageResource.insurer = {
			type: 'Organization',
			reference: `Organization/${payerUUID}`,
		};
	}

	// default order is unknown
	const order = _.find(PAYER_RESPONSIBILITY_OPTIONS, (item) => item?.value === model?.responsibilitySequence)?.order;
	coverageResource.order = order || null;

	// return the coverage resource
	return coverageResource;
};

const getTenantKeyValuePairs = (tenantPayers: Payers.ITenantPayer[]): Insurance.ITenantPayerOption[] => {
	return _.map(tenantPayers, (payer) => ({
		value: payer.id,
		label: payer.name,
	}));
};

export {
	getFilteredInsuranceInfo,
	getParsedCoverageInfo,
	getInitialModel,
	prepareSubmitModel,
	getExistingPayerResponsibility,
	getCategorizedInsurance,
	getTenantKeyValuePairs,
};
